// Frontend mirror of the backend role registry (src/authz/roles.js).
// Keep the two in step: these values are the DB CHECK-constraint values and the
// strings the API signs into the role claim, so a divergence here silently
// misroutes users rather than failing loudly.
//
// Roles were renamed to match the product vocabulary:
//   admin -> owner, manager -> property_manager, employee -> concierge,
//   tenant -> resident.
//
// TWO DIFFERENT COLUMNS (migration 037)
//
// `resident` is still part of the vocabulary, but it is no longer something a
// USER can be. It survives as something an INVITATION can say:
//
//   users.role        USER_ROLES    owner | property_manager | concierge | null
//   invitations.role  INVITE_ROLES  property_manager | concierge | resident
//
// Residency lives in occupancies, and the portal is reached through the
// `portal:read` permission the server derives from an open one — never through
// a role. So there are two normalisers: normalizeUserRole for a user's role,
// normalizeRole for an invitation's.
export const ROLES = {
  OWNER: 'owner',
  PROPERTY_MANAGER: 'property_manager',
  CONCIERGE: 'concierge',
  RESIDENT: 'resident',
  // Independent Stays IS5 (migration 180): a co-host works on the Stays
  // listings shared with them, nothing else (src/stays/routes/cohost-scope.js).
  STAYS_COHOST: 'stays_cohost',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_VALUES: Role[] = Object.values(ROLES);

// Human-facing labels, rendered instead of hardcoded strings so a future rename
// is a one-line change here plus a migration.
export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.OWNER]: 'Owner / Admin',
  [ROLES.PROPERTY_MANAGER]: 'Property Manager',
  [ROLES.CONCIERGE]: 'Concierge',
  [ROLES.RESIDENT]: 'Resident',
  [ROLES.STAYS_COHOST]: 'Co-host',
};

// Short labels for dense UI (table cells, badges) where the full label wraps.
export const ROLE_SHORT_LABELS: Record<Role, string> = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.PROPERTY_MANAGER]: 'Manager',
  [ROLES.CONCIERGE]: 'Concierge',
  [ROLES.RESIDENT]: 'Resident',
  [ROLES.STAYS_COHOST]: 'Co-host',
};

// Pre-rename values. Still accepted (not merely documented) because access
// tokens are signed for 15 minutes and carry the role claim: without this, every
// session issued before the migration would misroute until it expired.
export const LEGACY_ROLE_ALIASES: Record<string, Role> = {
  admin: ROLES.OWNER,
  manager: ROLES.PROPERTY_MANAGER,
  employee: ROLES.CONCIERGE,
  tenant: ROLES.RESIDENT,
};

// Map any accepted spelling onto its canonical value. Returns null for an
// unknown role so callers fail closed rather than defaulting to something.
export const normalizeRole = (role: string | null | undefined): Role | null => {
  if (!role) return null;
  const value = String(role).trim().toLowerCase();
  if ((ROLE_VALUES as string[]).includes(value)) return value as Role;

  return LEGACY_ROLE_ALIASES[value] || null;
};

export const isRole = (role: string | null | undefined): boolean =>
  normalizeRole(role) !== null;

// The users.role variant: canonicalize, then keep the result only if it is a
// staff capability. Mirrors normalizeUserRole in the backend and
// lb_user_staff_role in SQL. 'resident' and the legacy 'tenant' land on null —
// they describe a relationship, which is not this column's business.

export const roleLabel = (
  role: string | null | undefined,
  { short = false }: { short?: boolean } = {}
): string => {
  const value = normalizeRole(role);
  if (!value) return 'Unknown';

  return (short ? ROLE_SHORT_LABELS : ROLE_LABELS)[value];
};

// Seniority, low to high. Used to stop lateral/upward escalation: a role may
// only ever act on a role strictly below its own rank.
export const ROLE_RANK: Record<Role, number> = {
  [ROLES.RESIDENT]: 0,
  [ROLES.CONCIERGE]: 1,
  [ROLES.STAYS_COHOST]: 1,
  [ROLES.PROPERTY_MANAGER]: 2,
  [ROLES.OWNER]: 3,
};

export const outranks = (
  actorRole: string | null | undefined,
  targetRole: string | null | undefined
): boolean => {
  const a = normalizeRole(actorRole);
  const b = normalizeRole(targetRole);

  if (!a || !b) {
    return false;
  }

  return ROLE_RANK[a] > ROLE_RANK[b];
};

// Staff (organization-internal) roles — and, since migration 037, exactly the
// set of values users.role may hold. Anything else is null there.
export const STAFF_ROLES: Role[] = [
  ROLES.OWNER,
  ROLES.PROPERTY_MANAGER,
  ROLES.CONCIERGE,
  ROLES.STAYS_COHOST,
];

// What users.role accepts. An alias for STAFF_ROLES today, named separately
// because the two answer different questions.
export const USER_ROLES: readonly Role[] = STAFF_ROLES;

// What invitations.role accepts. `resident` lives HERE and not in USER_ROLES:
// an invitation states an intent, and that intent is what drives the unit
// guards and the occupancy the server creates on accept. Owner is absent
// because an organisation's owner is created by signup, never invited.
export const INVITE_ROLES: readonly Role[] = [
  ROLES.PROPERTY_MANAGER,
  ROLES.CONCIERGE,
  ROLES.RESIDENT,
  // IS5: a co-host invitation names the listings it shares (stays_listing_ids).
  ROLES.STAYS_COHOST,
];

export const isStaff = (role: string | null | undefined): boolean => {
  const value = normalizeRole(role);

  return value !== null && STAFF_ROLES.includes(value);
};

export const normalizeUserRole = (
  role: string | null | undefined
): Role | null => {
  const value = normalizeRole(role);

  return value !== null && STAFF_ROLES.includes(value) ? value : null;
};

// Which roles may issue invitations at all. An explicit allowlist and NOT
// derived from rank: rank alone would hand a Concierge the power to invite
// Residents. Inviting is a staff-management capability, not a consequence of
// outranking someone.
export const INVITER_ROLES: readonly Role[] = [
  ROLES.OWNER,
  ROLES.PROPERTY_MANAGER,
];

// Who may invite whom: an inviter may invite any INVITE_ROLE strictly below
// their own rank. Mirrors the backend derivation rather than restating the
// pairs, so the two cannot disagree — owner -> [property_manager, concierge,
// resident], property_manager -> [concierge, resident], everyone else -> [] —
// plus, since IS5, stays_cohost for both inviters (rank 1).
//
// The candidate list is INVITE_ROLES, not ROLE_VALUES. They give the same
// answer today, but deriving invitability from the general vocabulary is how
// 'resident' would silently vanish from every invite form the day it stopped
// being a user role.
//
// UI-only: the server re-checks this on every invite. Use it to decide which
// options a role picker offers, never as the authorization itself.
export const INVITABLE_ROLES: Record<Role, readonly Role[]> =
  ROLE_VALUES.reduce((acc, actor) => {
    acc[actor] = INVITER_ROLES.includes(actor)
      ? INVITE_ROLES.filter(target => outranks(actor, target))
      : [];

    return acc;
  }, {} as Record<Role, readonly Role[]>);

// IS5 (mirrors users.js ROLES_SET_HERE): the roles the People screens may set
// directly. A co-host is made only by invitation, on Stays → Team, because the
// invitation carries the listings they get (the server answers 400
// COHOST_BY_INVITATION otherwise).
export const ROLES_SET_HERE: readonly Role[] = USER_ROLES.filter(role => role !== ROLES.STAYS_COHOST);

// IS5: the co-host sees Lobbero Stays only (no Main Menu, no Desk).
export const isCohost = (role: string | null | undefined): boolean =>
  normalizeRole(role) === ROLES.STAYS_COHOST;
